import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, BarChart3, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { easerGet, errorMessage, fmtTime, fmtMinutes, toISODate } from '../../lib/easerApi';

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(12, 0, 0, 0); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1, 12);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 12);
const fmtDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });

/** Worked-hours recap: day clock-ins vs planned slots, by week or month. */
export function EaserHours({ onUnauthorized }) {
  const [mode, setMode] = useState('week');
  const [anchor, setAnchor] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const anchorKey = toISODate(anchor);
  const range = useMemo(() => {
    const a = new Date(`${anchorKey}T12:00:00`);
    return mode === 'week' ? { start: startOfWeek(a), end: addDays(startOfWeek(a), 6) } : { start: startOfMonth(a), end: endOfMonth(a) };
  }, [mode, anchorKey]);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await easerGet('/api/easer/hours', { start: toISODate(range.start), end: toISODate(range.end) });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized();
      setError(errorMessage(err, 'Impossible de charger le récap'));
      setData({ days: [], totals: {} });
    }
  }, [range, onUnauthorized]);

  useEffect(() => { setData(null); load(); }, [load]);

  const shift = (n) => setAnchor((a) => (mode === 'week' ? addDays(a, 7 * n) : new Date(a.getFullYear(), a.getMonth() + n, 1, 12)));
  const title = mode === 'week'
    ? `Semaine du ${range.start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} au ${range.end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`
    : anchor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const t = data?.totals || {};
  const diff = (t.actual_minutes || 0) - (t.planned_minutes || 0);
  const activeDays = (data?.days || []).filter((d) => d.day_start || d.planned_count > 0 || d.slots?.length > 0);

  return (
    <Card className="p-5 border-border shadow-sm" data-testid="easer-hours">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="font-['Space_Grotesk'] font-semibold flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Récap des heures</h3>
        <div className="ml-auto flex rounded-lg border border-border bg-card p-0.5">
          {[['week', 'Semaine'], ['month', 'Mois']].map(([id, label]) => (
            <button key={id} type="button" onClick={() => setMode(id)} data-testid={`hours-mode-${id}`}
              className={`px-3 h-8 rounded-md text-xs font-medium transition-colors ${mode === id ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:text-foreground'}`}>{label}</button>
          ))}
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={load} aria-label="Actualiser" data-testid="hours-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      <div className="flex items-center justify-between mb-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(-1)} aria-label="Précédent" data-testid="hours-prev"><ChevronLeft className="h-5 w-5" /></Button>
        <p className="text-sm font-medium first-letter:uppercase text-center" data-testid="hours-title">{title}</p>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(1)} aria-label="Suivant" data-testid="hours-next"><ChevronRight className="h-5 w-5" /></Button>
      </div>

      {!data && <div className="space-y-2"><Skeleton className="h-20 rounded-xl" /><Skeleton className="h-24 rounded-xl" /></div>}
      {data && error && <p className="text-sm text-destructive" data-testid="hours-error">{error}</p>}

      {data && !error && (
        <>
          <div className="grid grid-cols-3 gap-2 mb-2">
            <div className="rounded-xl bg-[hsl(var(--secondary))]/60 p-3">
              <p className="text-[11px] text-muted-foreground">Prévu (planning)</p>
              <p className="text-xl font-bold tabular-nums" data-testid="hours-total-planned">{fmtMinutes(t.planned_minutes)}</p>
              <p className="text-[11px] text-muted-foreground">{t.planned_count || 0} créneau{t.planned_count > 1 ? 'x' : ''}</p>
            </div>
            <div className="rounded-xl bg-[hsl(var(--secondary))]/60 p-3">
              <p className="text-[11px] text-muted-foreground">Réalisé (pointages)</p>
              <p className="text-xl font-bold text-[hsl(var(--primary))] tabular-nums" data-testid="hours-total-actual">{fmtMinutes(t.actual_minutes)}</p>
              <p className="text-[11px] text-muted-foreground">{t.clocked_count || 0} pointé{t.clocked_count > 1 ? 's' : ''} · {t.done_count || 0} terminé{t.done_count > 1 ? 's' : ''}</p>
            </div>
            <div className="rounded-xl bg-[hsl(var(--secondary))]/60 p-3">
              <p className="text-[11px] text-muted-foreground">Écart</p>
              <p className={`text-xl font-bold tabular-nums ${diff >= 0 ? 'text-[hsl(var(--brand-sage))]' : 'text-[hsl(var(--accent))]'}`} data-testid="hours-total-diff">{diff >= 0 ? '+' : '−'}{fmtMinutes(Math.abs(diff))}</p>
              <p className="text-[11px] text-muted-foreground">réalisé − prévu</p>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mb-3" data-testid="hours-total-day">
            Journées pointées : <strong className="text-foreground">{fmtMinutes(t.day_minutes)}</strong> ({t.days_clocked || 0} jour{t.days_clocked > 1 ? 's' : ''}) · pointages lus dans l'agenda Dolibarr, y compris ceux faits avec l'application mobile.
          </p>
          {data.pointages_available === false && <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Pointages Dolibarr indisponibles : seuls les pointages faits sur le site sont comptés.</p>}
          {data.planning_available === false && <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Planning Dolibarr indisponible : prévisionnel non calculé.</p>}

          {activeDays.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="hours-empty">Aucun pointage ni créneau sur cette période.</p>
          ) : (
            <ul className="divide-y divide-border" data-testid="hours-days">
              {activeDays.map((d) => (
                <li key={d.day} className="py-2.5 text-sm" data-testid="hours-day">
                  <div className="flex items-center gap-2">
                    <span className="capitalize font-medium w-24">{fmtDay(d.day)}</span>
                    <span className="text-muted-foreground tabular-nums text-xs flex-1">{d.day_start ? `journée ${fmtTime(d.day_start)} → ${fmtTime(d.day_end)}` : `${d.planned_count} créneau${d.planned_count > 1 ? 'x' : ''} · prévu ${fmtMinutes(d.planned_minutes)}`}</span>
                    {d.incidents > 0 && <span className="inline-flex items-center gap-0.5 text-[11px] text-[hsl(var(--accent))]"><AlertTriangle className="h-3 w-3" /> {d.incidents}</span>}
                    <span className="font-semibold text-[hsl(var(--primary))] tabular-nums w-14 text-right">{d.actual_minutes ? fmtMinutes(d.actual_minutes) : '—'}</span>
                  </div>
                  {d.slots?.length > 0 && (
                    <ul className="mt-1 pl-24 space-y-0.5">
                      {d.slots.map((s) => (
                        <li key={s.id} className="text-xs text-muted-foreground flex items-center gap-2" data-testid="hours-slot">
                          <span className="truncate flex-1">{s.label}</span>
                          <span className="tabular-nums whitespace-nowrap">{s.clock_in ? `${fmtTime(s.clock_in)} → ${s.clock_out ? fmtTime(s.clock_out) : '…'}` : 'non pointé'}</span>
                          <span className="tabular-nums w-12 text-right">{s.actual_minutes ? fmtMinutes(s.actual_minutes) : ''}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}
